import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Content, Header, Main } from "../commons/Template/Template";
import { Form, FormLoader } from "../commons/Form/Form";
import api from "../../api/resources";
import { ArrowRight, Plus, User } from "react-feather";
import { dashboardRoute } from "../../routes/routes";
import { useToastsDispatch } from "../commons/Toasts/ToastsContext";
import { useCountries } from "../../hooks/commons/useCountries";
import {
  FancyButtons,
  FancyButton,
} from "../commons/FancyButtons/FancyButtons";
import { formatDate } from "../../format";
import Button from "../commons/Button/Button";
import "./CompanyReps.css";
import Typo, { Em } from "../commons/Typo/Typo";

export default function CompanyReps() {
  const { t, i18n } = useTranslation(["common", "company"]);
  document.title = t("company:reps.title");
  const countries = useCountries();
  const dispatchToast = useToastsDispatch();
  const [rep, setRep] = useState(null);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);

  const init = async () => {
    setLoading(true);
    const response = await api.listReps();
    if (response.ok) {
      let JSONReps = await response.json();
      let newReps = [];
      if (JSONReps?.length) {
        for (const rep of JSONReps) {
          delete rep.id_card;
          const newRep = {
            ...rep,
            nationality: {
              label: countries.getName(rep.nationality, i18n.resolvedLanguage),
              value: rep.nationality,
            },
          };
          newReps.push(newRep);
        }
        setReps(newReps);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const handleSelect = (
    rep = {
      id: "",
      full_name: "",
      birth_date: "",
      nationality: null,
      email: "",
      telephone: "",
    },
  ) => setRep(rep);

  const handleChange = (newValue, targetField) =>
    setRep((prev) => ({ ...prev, [targetField]: newValue }));

  const handleSuccess = (type = "create") => {
    init();
    dispatchToast({
      type: "add",
      variant: "success",
      heading: t(`company:reps.verifyRep.form.message.${type}.title`),
      subheading: t(`company:reps.verifyRep.form.message.${type}.content`),
      className: "CompanyRepsSuccess",
    });
    setRep(null);
  };

  if (rep)
    return (
      <Rep
        rep={rep}
        onChange={handleChange}
        onClear={() => setRep(null)}
        onSuccess={handleSuccess}
      />
    );
  return <Reps loading={loading} reps={reps} onSelect={handleSelect} />;
}

function Reps({ loading, reps, onSelect }) {
  const { t } = useTranslation(["common", "company"]);

  return (
    <Main>
      <Header>
        <Typo variant="h1">
          <Trans
            t={t}
            i18nKey={"company:reps.home.heading"}
            components={{
              emphasis: <Em />,
            }}
          />
        </Typo>
      </Header>
      <Content>
        {loading ? (
          <FormLoader />
        ) : (
          <FancyButtons>
            {reps.map((rep, index) => {
              return (
                <FancyButton
                  key={index}
                  heading={rep.full_name}
                  icon={<User />}
                  iconConf={<ArrowRight />}
                  onClick={() => onSelect(rep)}
                />
              );
            })}
            <FancyButton
              heading={t("company:reps.home.button.add")}
              icon={<Plus />}
              iconConf={<ArrowRight />}
              onClick={() => onSelect()}
            />
            <Button
              variant={"primary_a"}
              style={{ lineHeight: "2" }}
              text={t("company:reps.home.button.continue")}
              onClick={() => (window.location.href = dashboardRoute)} // not using navigate because app needs refresh (active company is switched on backend)
            />
          </FancyButtons>
        )}
      </Content>
    </Main>
  );
}

function Rep({ rep, onChange, onClear, onSuccess }) {
  const { t, i18n } = useTranslation(["common", "company"]);
  const countries = useCountries();

  const initialErrors = {
    full_name: [],
    birth_date: [],
    nationality: [],
    email: [],
    telephone: [],
    non_field_errors: [],
  };

  const [errors, setErrors] = useState(initialErrors);

  const clearErrors = () => {
    setErrors(initialErrors);
  };

  const handleSubmit = async () => {
    clearErrors();

    // If rep. has an ID, then it is an update (patch)
    if (rep.id) {
      const response = await api.updateRep(rep.id, {
        ...rep,
        nationality: rep.nationality.value,
      });
      if (response.ok) {
        onSuccess("update");
      } else {
        const jsonErrors = await response.json();
        setErrors(jsonErrors);
      }

      // If there is no ID, it is a creation (post)
    } else {
      const response = await api.createRep({
        ...rep,
        nationality: rep.nationality.value,
      });
      if (response.ok) {
        onSuccess("create");
      } else {
        const jsonErrors = await response.json();
        setErrors(jsonErrors);
      }
    }
  };

  const nationalityOptions = Object.entries(
    countries.getNames(i18n.resolvedLanguage, { select: "official" }),
  ).map(([alpha2Code, name]) => {
    return {
      label: name,
      value: countries.alpha2ToAlpha3(alpha2Code),
    };
  });

  const nationalityInput = {
    label: t("company:reps.verifyRep.form.nationality"),
    type: "select",
    name: "nationality",
    value: rep.nationality,
    onChange: (value) => onChange(value, "nationality"),
    errors: errors.nationality,
    options: nationalityOptions,
    placeholder: rep.nationality
      ? countries.getName(rep.nationality, i18n.resolvedLanguage)
      : t("company:reps.verifyRep.form.nationality"),
  };

  const fullNameInput = {
    label: t("company:reps.verifyRep.form.surname"),
    type: "text",
    name: "full_name",
    value: rep.full_name,
    onChange: (value) => onChange(value, "full_name"),
    errors: errors.name,
  };

  const birthDateInput = {
    label: t("company:reps.verifyRep.form.birthDate"),
    type: "date",
    name: "birth_date",
    value: rep.birth_date,
    onChange: (value) => onChange(value, "birth_date"),
    errors: errors.birth_date,
  };

  const emailInput = {
    label: t("company:reps.verifyRep.form.email"),
    type: "email",
    name: "email",
    value: rep.email,
    onChange: (value) => onChange(value, "email"),
    errors: errors.email,
    required: false,
  };

  const telephoneInput = {
    label: t("company:reps.verifyRep.form.tel"),
    type: "tel",
    name: "telephone",
    value: rep.telephone && rep.telephone.replaceAll(" ", ""),
    onChange: (value) => onChange(value, "telephone"),
    errors: errors.telephone,
  };

  const inputs = [
    {
      data: [fullNameInput],
    },
    {
      data: [birthDateInput, nationalityInput],
      display: "grid",
    },
    { data: [emailInput, telephoneInput], display: "grid" },
  ];

  const patchInputs = [
    {
      data: [nationalityInput, emailInput, telephoneInput],
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
    },
  ];

  return (
    <Main themeSwitch style={{ alignItems: "center", width: "fit-content" }}>
      <Header
        onBackButtonClick={onClear}
        containerStyle={{ alignSelf: "flex-start" }}
      >
        <Typo variant="h1">
          {rep.full_name ? rep.full_name : t("company:reps.verifyRep.heading")}
        </Typo>
        {rep.birth_date && (
          <Typo variant="body2">
            <Trans
              t={t}
              i18nKey={"company:reps.verifyRep.subheading"}
              values={{
                birth_date: formatDate(rep.birth_date, i18n.resolvedLanguage),
              }}
            />
          </Typo>
        )}
      </Header>
      <Content>
        <Form
          className={"CompanyRepsForm"}
          style={{ maxWidth: rep.id ? "45rem" : "40rem" }}
          button={t("company:reps.verifyRep.form.button")}
          onSubmit={handleSubmit}
          errors={errors.non_field_errors}
          inputs={rep.id ? patchInputs : inputs}
        />
      </Content>
    </Main>
  );
}
